import React, { FC, useEffect, useState, Fragment, useMemo } from 'react';
import './index.scss';
import {
  Button,
  Checkbox,
  Dropdown,
  List,
  Radio,
  SearchBox,
  Bubble,
} from 'tea-component';
import { cloneDeep, constant, divide, reduce } from 'lodash-es';
import GameImage from '@/pages/home/components/GameImage';

import { useIntl, getLocale } from 'umi';
import { deepCompare } from '../../_config/utils';

type LevelType = 'S' | 'A' | 'B' | 'C';
type GameLevelValueType = {
  [key in LevelType]: string[];
};

interface GameLevelOptions {
  value: LevelType;
  text: string;
  list: {
    value: string;
    text: string;
    img: string;
  }[];
}

interface GameLevelSelectProps {
  options: GameLevelOptions[];
  value: GameLevelValueType;
  onChange: (value: GameLevelValueType) => void;
}

const defaultValue: GameLevelValueType = {
  S: [],
  A: [],
  B: [],
  C: [],
};

const GameLevelSelect: FC<GameLevelSelectProps> = (props) => {
  const { options, value, onChange } = props;
  const [all, setAll] = useState(false);
  const { formatMessage } = useIntl();
  const locale = getLocale();
  const [displayOptions, setDisplayOptions] = useState<GameLevelOptions[]>([]);
  const [searchValue, setSearchValue] = useState('');
  const [titleNow, setTitleNow] = useState('');

  const [currentValue, setCurrentValue] = useState<GameLevelValueType>(
    defaultValue,
  );

  useEffect(() => {
    setCurrentValue(value || defaultValue);
  }, [value]);

  useEffect(() => {
    const optionTotal = options.reduce(
      (result, item) => (result += item.list.length),
      0,
    );
    const valueTotal = Object.values(currentValue).reduce(
      (result, item) => (result += item.length),
      0,
    );
    if (optionTotal === valueTotal && valueTotal !== 0) {
      setAll(true);
      setTitleNow(formatMessage({ id: 'dbi_all' }));
    } else if (optionTotal === valueTotal && valueTotal === 0) {
      setAll(false);
      setTitleNow('');
    } else {
      const titleList = reduce(
        currentValue,
        (result, value, key) => {
          if (value?.length) {
            result.push(`${key}(${value?.length})`);
          }
          return result;
        },
        [] as string[],
      );
      setTitleNow(titleList.join(','));
      setAll(false);
    }
  }, [currentValue, locale]);
  useEffect(() => {
    setDisplayOptions(options);
  }, [options]);

  /** 全选 */
  const handleSelectAll = () => {
    if (all) {
      setCurrentValue(defaultValue);
    } else {
      const allValue = options?.reduce((result, item) => {
        result[item.value] = item.list.map((config) => config.value);
        return result;
      }, {} as any);
      setCurrentValue(allValue);
    }
    setAll(!all);
  };

  /** 等级全选 */
  const handleSelectLevel = (value: LevelType) => {
    if (currentValue?.[value]?.length) {
      const copyValue = cloneDeep(currentValue);
      copyValue[value] = [];
      setCurrentValue(copyValue);
      setAll(false);
    } else {
      const copyValue = cloneDeep(currentValue);
      const targetLevel = options?.find((item) => item.value === value);
      if (targetLevel) {
        copyValue[value] = targetLevel?.list?.map((item) => item.value) || [];
        setCurrentValue(copyValue);
      }
    }
  };

  /** 单个选择 */
  const handleCheckboxChange = (
    value: boolean,
    level: LevelType,
    gameId: string,
  ) => {
    if (value) {
      const copyValue = cloneDeep(currentValue);
      copyValue[level].push(gameId);
      setCurrentValue(copyValue);
    } else {
      const copyValue = cloneDeep(currentValue);
      const index = copyValue?.[level]?.findIndex((item) => item === gameId);
      if (index > -1) {
        copyValue?.[level]?.splice(index, 1);
        setCurrentValue(copyValue);
      }
    }
  };

  const handleSearch = (value: string) => {
    setSearchValue(value);
    if (value.replace(/(^\s*)|(\s*$)/g, '')) {
      const reg = new RegExp(value.replace(/(^\s*)|(\s*$)/g, ''), 'ig');
      const list = options.map((item) => {
        return {
          ...item,
          list: item?.list?.filter((item) => reg.test(item.text)),
        };
      });
      setDisplayOptions(list);
    } else {
      setDisplayOptions(options);
    }
  };

  const titleNowBubble = useMemo(() => {
    const targetOptions = options?.map((item) => {
      return {
        ...item,
        list: item.list
          .filter((config) =>
            currentValue?.[item.value]?.includes(config.value),
          )
          .map((item) => item.text),
      };
    });
    return (
      <div className="game-level-title-popper">
        {targetOptions.map((item, index) => {
          return item.list.length > 0 ? (
            <div key={index} className="game-level-title-popper-item">
              <div className="game-level-title-popper-item-title">
                {item.value}
              </div>
              <div className="game-level-title-popper-item-value">
                {item.list.join(',')}
              </div>
            </div>
          ) : null;
        })}
      </div>
    );
  }, [currentValue]);

  const handleOpenChange = (val: boolean) => {
    if (!val) {
      const value1 = reduce(
        value,
        (result, v, key) => {
          result[key] = v.sort(
            (a: any, b: any) => a?.charCodeAt() - b?.charCodeAt(),
          );
          return result;
        },
        {},
      );
      const value2 = reduce(
        currentValue,
        (result, v, key) => {
          result[key] = v.sort(
            (a: any, b: any) => a?.charCodeAt() - b?.charCodeAt(),
          );
          return result;
        },
        {},
      );
      const result = deepCompare(value1, value2);

      const valueTotal = Object.values(currentValue).reduce(
        (result, item) => (result += item.length),
        0,
      );

      if (!result && valueTotal === 0) {
        const allValue = options?.reduce((result, item) => {
          result[item.value] = item?.list?.map((config) => config.value);
          return result;
        }, {} as any);
        setCurrentValue(allValue);
        setAll(true);
        onChange && onChange(allValue);
        return;
      }

      if (!result) {
        onChange && onChange(currentValue);
      }
    }
  };

  const hasNoSearchResult = useMemo(() => {
    return displayOptions.every((item) => !item?.list?.length);
  }, [displayOptions]);

  return (
    <Dropdown
      className="dbi-game-level-select"
      overlayClassName="dbi-game-level-select-overlay"
      matchButtonWidth
      button={
        <div className="dbi-select-button">
          <span className="select-button-text">
            {formatMessage({ id: 'dbi_benchmark_game_level' })}
          </span>
          <Bubble
            dark
            content={titleNowBubble}
            placement="right"
            className="dbi-mobile-benchmark-game-level-title-bubble"
          >
            <div className="select-button-value">{titleNow}</div>
          </Bubble>
        </div>
      }
      onOpenChange={handleOpenChange}
      appearance="button"
      clickClose={false}
    >
      {(close) => (
        <div className="dbi-game-level-box">
          <SearchBox
            value={searchValue}
            onChange={handleSearch}
            placeholder={formatMessage({ id: 'dbi_label_search' })}
            className="dbi-game-leve-search"
          />
          {hasNoSearchResult && searchValue ? (
            <div className="dbi-game-level-no-data">
              {formatMessage({ id: 'dbi_benchmark_no_data_yet' })}
            </div>
          ) : (
            <>
              <Radio
                className="dbi-game-level-all"
                value={all}
                onChange={handleSelectAll}
              >
                {formatMessage({ id: 'dbi_all' })}
              </Radio>
              <List className="dbi-game-level-list" type="option">
                {displayOptions.map((item, index) => {
                  return item?.list?.length > 0 ? (
                    <Fragment key={index}>
                      <div className="dbi-game-level-group">
                        <span className="dbi-group-text">{formatMessage({ id: 'dbi_benchmark_tier' }, { tier: item.text })}</span>
                        {/* <Button
                          type="link"
                          onClick={() => handleSelectLevel(item.value)}
                        >
                          {formatMessage({
                            id: !currentValue?.[item.value]?.length
                              ? 'dbi_select_all'
                              : 'dbi_clear_all',
                          })}
                        </Button> */}
                      </div>
                      <List.Item>
                        <Checkbox
                          value={currentValue?.[item.value]?.length === item?.list?.length}
                          indeterminate={currentValue?.[item.value]?.length > 0 && 
                            currentValue?.[item.value]?.length !== item?.list?.length}
                          onChange={() => handleSelectLevel(item.value)}>
                          {formatMessage({id: 'dbi_all'})}
                        </Checkbox>
                      </List.Item>
                      {item?.list?.map((config, i) => {
                        return (
                          <List.Item key={i}>
                            <Checkbox
                              display="block"
                              onChange={(value) =>
                                handleCheckboxChange(
                                  value,
                                  item.value,
                                  config.value,
                                )
                              }
                              value={
                                !!currentValue?.[item.value]?.includes(
                                  config.value,
                                )
                              }
                            >
                              <div className="dbi-game-level-item">
                                <GameImage
                                  src={config?.img}
                                  entityType={'mobile'}
                                  className="dbi-game-level-img"
                                />
                                <span className="dbi-game-level-text">
                                  {config?.text}
                                </span>
                              </div>
                            </Checkbox>
                          </List.Item>
                        );
                      })}
                    </Fragment>
                  ) : null;
                })}
              </List>
            </>
          )}
        </div>
      )}
    </Dropdown>
  );
};
GameLevelSelect.defaultProps = {
  options: [],
};

export default GameLevelSelect;

// const ItemBox = (props: any) => {
//   let { item } = props;
//   return (
//     <>
//       <div className="dbi-game-level-group">
//         <span className="dbi-group-text">{item.text}</span>
//         <Button type="link">{formatMessage({ id: 'dbi_select_all' })}</Button>
//       </div>
//       {item.list.map((e, i) => {
//         return (
//           <List.Item key={i}>
//             <Checkbox display="block">
//               <div className="dbi-game-level-item">
//                 <img src={e.img} className="dbi-game-level-img" />
//                 <span className="dbi-game-level-text">{e.text}</span>
//               </div>
//             </Checkbox>
//           </List.Item>
//         );
//       })}
//     </>
//   );
// };
